import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { PUBLIC_EMAILS } from "../config/publicEmails";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { 
  Building2, Handshake, Gift, Package, Users, Mail, Phone, Globe, 
  MapPin, FileText, Upload, CheckCircle, ArrowRight, Sparkles, X, Paperclip, Loader2
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Configuration des fichiers
const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 Mo
const ALLOWED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png", ".doc", ".docx"];
const ALLOWED_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
];

const requestTypes = [
  { value: "reduction", label: "Réduction pour les adhérents", icon: Gift, description: "Proposer des tarifs préférentiels à nos adhérents" },
  { value: "partner_offer", label: "Offre partenaire", icon: Handshake, description: "Devenir partenaire officiel d'ITERCATUS" },
  { value: "product_test", label: "Produit à tester", icon: Package, description: "Soumettre un produit pour un test/avis" },
  { value: "other", label: "Autre collaboration", icon: Sparkles, description: "Proposer une autre forme de collaboration" },
];

export default function PartnerRequestPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const [attachments, setAttachments] = useState([]); // {file, url, name, size, uploading, error}
  
  const [form, setForm] = useState({
    request_type: "",
    company_name: "",
    contact_name: "",
    contact_function: "",
    email: "",
    phone: "",
    website: "",
    address_street: "",
    address_city: "",
    address_postal_code: "",
    address_country: "France",
    description: "",
    conditions: "",
  });

  // Validation d'un fichier
  const validateFile = (file) => {
    const ext = "." + file.name.split(".").pop().toLowerCase();
    
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `Format non autorisé: ${file.name}. Formats acceptés: PDF, JPG, PNG, DOC, DOCX`;
    }
    
    if (file.size > MAX_FILE_SIZE) {
      return `Fichier trop volumineux: ${file.name}. Maximum: 10 Mo`;
    }
    
    return null;
  };

  // Upload d'un fichier
  const uploadFile = async (file, index) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/api/partner-requests/upload-attachment`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      return {
        url: response.data.url,
        file_name: response.data.file_name,
        file_size: response.data.file_size,
        file_type: response.data.file_type
      };
    } catch (error) {
      console.error("Upload error:", error);
      throw new Error(error.response?.data?.detail || "Erreur lors de l'upload");
    }
  };

  // Gestion de l'ajout de fichiers
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    
    if (attachments.length + files.length > MAX_FILES) {
      toast.error(`Maximum ${MAX_FILES} fichiers autorisés`);
      return;
    }

    // Valider les fichiers
    for (const file of files) {
      const error = validateFile(file);
      if (error) {
        toast.error(error);
        return;
      }
    }

    setUploading(true);

    // Upload chaque fichier
    for (const file of files) {
      const tempId = Date.now() + Math.random();
      
      // Ajouter en état "uploading"
      setAttachments(prev => [...prev, {
        id: tempId,
        name: file.name,
        size: file.size,
        uploading: true,
        error: null,
        url: null
      }]);

      try {
        const result = await uploadFile(file);
        
        // Mettre à jour avec l'URL
        setAttachments(prev => prev.map(a => 
          a.id === tempId 
            ? { ...a, uploading: false, url: result.url, file_name: result.file_name, file_size: result.file_size, file_type: result.file_type }
            : a
        ));
      } catch (error) {
        // Marquer comme erreur
        setAttachments(prev => prev.map(a => 
          a.id === tempId 
            ? { ...a, uploading: false, error: error.message }
            : a
        ));
        toast.error(`Erreur upload: ${file.name}`);
      }
    }

    setUploading(false);
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Supprimer une pièce jointe
  const removeAttachment = (id) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  // Formater la taille
  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + " o";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " Ko";
    return (bytes / (1024 * 1024)).toFixed(1) + " Mo";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Vérifier qu'aucun upload n'est en cours
    if (attachments.some(a => a.uploading)) {
      toast.error("Veuillez attendre la fin des uploads");
      return;
    }

    // Vérifier les erreurs d'upload
    const failedUploads = attachments.filter(a => a.error);
    if (failedUploads.length > 0) {
      toast.error("Certains fichiers n'ont pas pu être uploadés. Veuillez les retirer.");
      return;
    }

    setLoading(true);

    try {
      // Préparer les pièces jointes
      const successfulAttachments = attachments.filter(a => a.url);
      
      await axios.post(`${API}/api/partner-requests`, {
        ...form,
        request_type: selectedType,
        attached_documents: successfulAttachments.map(a => a.url),
        attached_documents_metadata: successfulAttachments.map(a => ({
          file_name: a.file_name || a.name,
          file_size: a.file_size || a.size,
          file_type: a.file_type || "unknown",
          url: a.url
        }))
      });

      setSubmitted(true);
      toast.success("Demande envoyée ! Nous avons bien reçu votre proposition et reviendrons vers vous rapidement.");
    } catch (error) {
      console.error("Error submitting request:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-secondary py-20">
        <div className="max-w-2xl mx-auto px-6">
          <Card className="border-0 shadow-xl">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="font-heading text-3xl font-bold text-primary mb-4">
                Merci pour votre proposition !
              </h1>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                Nous avons bien reçu votre demande de partenariat. Notre équipe l'étudiera 
                avec attention et reviendra vers vous dans les meilleurs délais.
              </p>
              <div className="bg-secondary rounded-lg p-6 mb-8 text-left">
                <p className="text-sm font-medium text-primary mb-2">Pour toute question :</p>
                <p className="text-sm text-muted-foreground">
                  ITERCATUS<br />
                  Attention Gilles Fouque<br />
                  Quartier les 3 Fonds<br />
                  13124 Peypin<br />
                  <a href={`mailto:${PUBLIC_EMAILS.partners}`} className="text-accent hover:underline">
                    {PUBLIC_EMAILS.partners}
                  </a>
                </p>
              </div>
              <Link to="/">
                <Button className="bg-accent hover:bg-accent/90">
                  Retour à l'accueil
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary">
      {/* Hero Section */}
      <section className="bg-primary text-white py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full mb-6">
            <Building2 className="h-4 w-4" />
            <span className="text-sm font-medium">Espace Entreprises</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            Devenez partenaire d'ITERCATUS
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
            Vous êtes une entreprise et souhaitez offrir des réductions à nos adhérents 
            ou proposer un test produit ? Remplissez ce formulaire et notre équipe 
            étudiera votre proposition.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 border-b border-border">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-7 w-7 text-accent" />
              </div>
              <h3 className="font-heading font-semibold text-primary mb-2">Adhérents engagés</h3>
              <p className="text-sm text-muted-foreground">
                Une communauté de voyageurs passionnés et actifs
              </p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="h-7 w-7 text-accent" />
              </div>
              <h3 className="font-heading font-semibold text-primary mb-2">Visibilité éditoriale</h3>
              <p className="text-sm text-muted-foreground">
                Mise en avant dans nos articles et nos communications
              </p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Handshake className="h-7 w-7 text-accent" />
              </div>
              <h3 className="font-heading font-semibold text-primary mb-2">Partenariat durable</h3>
              <p className="text-sm text-muted-foreground">
                Une collaboration basée sur la confiance et la qualité
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6">
          <form onSubmit={handleSubmit} className="space-y-10">
            
            {/* Step 1: Type de proposition */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="w-8 h-8 bg-accent text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
                  Type de proposition
                </CardTitle>
                <CardDescription>
                  Sélectionnez le type de collaboration que vous souhaitez nous proposer
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {requestTypes.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setSelectedType(type.value)}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        selectedType === type.value
                          ? "border-accent bg-accent/5"
                          : "border-border hover:border-accent/50"
                      }`}
                    >
                      <type.icon className={`h-6 w-6 mb-2 ${
                        selectedType === type.value ? "text-accent" : "text-muted-foreground"
                      }`} />
                      <p className="font-medium text-primary">{type.label}</p>
                      <p className="text-xs text-muted-foreground mt-1">{type.description}</p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Step 2: Informations entreprise */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="w-8 h-8 bg-accent text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                  Informations entreprise
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="company_name">Nom de l'entreprise *</Label>
                    <Input
                      id="company_name"
                      value={form.company_name}
                      onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                      placeholder="Ex: MSC Croisières"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_name">Nom du contact *</Label>
                    <Input
                      id="contact_name"
                      value={form.contact_name}
                      onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                      placeholder="Prénom et nom"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_function">Fonction</Label>
                    <Input
                      id="contact_function"
                      value={form.contact_function}
                      onChange={(e) => setForm({ ...form, contact_function: e.target.value })}
                      placeholder="Ex: Responsable partenariats"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="pl-10"
                        placeholder="contact@entreprise.com"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="phone">Téléphone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="pl-10"
                        placeholder="+33 1 23 45 67 89"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="website">Site web</Label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="website"
                      type="url"
                      value={form.website}
                      onChange={(e) => setForm({ ...form, website: e.target.value })}
                      className="pl-10"
                      placeholder="https://www.entreprise.com"
                    />
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <Label className="flex items-center gap-2 mb-3">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    Adresse complète
                  </Label>
                  <div className="space-y-4">
                    <Input
                      value={form.address_street}
                      onChange={(e) => setForm({ ...form, address_street: e.target.value })}
                      placeholder="Rue et numéro"
                    />
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <Input
                        value={form.address_postal_code}
                        onChange={(e) => setForm({ ...form, address_postal_code: e.target.value })}
                        placeholder="Code postal"
                      />
                      <Input
                        value={form.address_city}
                        onChange={(e) => setForm({ ...form, address_city: e.target.value })}
                        placeholder="Ville"
                        className="md:col-span-2"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Step 3: Votre proposition */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="w-8 h-8 bg-accent text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                  Votre proposition
                </CardTitle>
                <CardDescription>
                  Décrivez en détail ce que vous souhaitez proposer à nos adhérents
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label htmlFor="description">Description détaillée *</Label>
                  <Textarea
                    id="description"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Décrivez votre proposition : type d'offre, avantages proposés, public ciblé, etc."
                    rows={6}
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Minimum 50 caractères. Soyez précis pour nous permettre d'évaluer votre proposition.
                  </p>
                </div>

                <div>
                  <Label htmlFor="conditions">Conditions éventuelles</Label>
                  <Textarea
                    id="conditions"
                    value={form.conditions}
                    onChange={(e) => setForm({ ...form, conditions: e.target.value })}
                    placeholder="Conditions d'application, durée de validité, restrictions, etc."
                    rows={3}
                  />
                </div>

                {/* Pièces jointes */}
                <div className="space-y-3 pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      <Paperclip className="h-4 w-4" />
                      Pièces jointes (optionnel)
                    </Label>
                    <span className="text-xs text-muted-foreground">
                      {attachments.length}/{MAX_FILES} fichiers
                    </span>
                  </div>
                  
                  <p className="text-xs text-muted-foreground">
                    Plaquette, logo, proposition commerciale, visuels... (PDF, JPG, PNG, DOC - Max 10 Mo/fichier)
                  </p>

                  {/* Zone d'upload */}
                  <div 
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                      attachments.length >= MAX_FILES 
                        ? "border-muted bg-muted/20 cursor-not-allowed" 
                        : "border-border hover:border-accent cursor-pointer"
                    }`}
                    onClick={() => attachments.length < MAX_FILES && fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={handleFileSelect}
                      className="hidden"
                      disabled={attachments.length >= MAX_FILES}
                    />
                    {uploading ? (
                      <div className="flex items-center justify-center gap-2 text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>Upload en cours...</span>
                      </div>
                    ) : attachments.length >= MAX_FILES ? (
                      <p className="text-sm text-muted-foreground">
                        Nombre maximum de fichiers atteint
                      </p>
                    ) : (
                      <>
                        <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">
                          Cliquez ou glissez vos fichiers ici
                        </p>
                      </>
                    )}
                  </div>

                  {/* Liste des fichiers */}
                  {attachments.length > 0 && (
                    <div className="space-y-2">
                      {attachments.map((attachment) => (
                        <div 
                          key={attachment.id}
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            attachment.error 
                              ? "bg-red-50 border-red-200" 
                              : attachment.uploading 
                                ? "bg-blue-50 border-blue-200"
                                : "bg-secondary border-border"
                          }`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <FileText className={`h-5 w-5 flex-shrink-0 ${
                              attachment.error ? "text-red-500" : "text-muted-foreground"
                            }`} />
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">
                                {attachment.name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {attachment.uploading 
                                  ? "Upload en cours..." 
                                  : attachment.error 
                                    ? attachment.error
                                    : formatSize(attachment.size)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {attachment.uploading && (
                              <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                            )}
                            {attachment.url && (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeAttachment(attachment.id)}
                              className="h-8 w-8 p-0 hover:bg-red-100"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Submit */}
            <div className="flex flex-col items-center gap-4 pt-6">
              <Button
                type="submit"
                size="lg"
                disabled={loading || uploading || !selectedType || !form.company_name || !form.contact_name || !form.email || !form.description}
                className="bg-accent hover:bg-accent/90 px-12 py-6 text-lg rounded-full"
              >
                {loading ? "Envoi en cours..." : "Envoyer ma proposition"}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <p className="text-xs text-muted-foreground text-center max-w-md">
                En soumettant ce formulaire, vous acceptez que nous conservions vos informations 
                dans le cadre de l'étude de votre proposition de partenariat.
              </p>
            </div>
          </form>
        </div>
      </section>

      {/* Contact Info */}
      <section className="bg-primary text-white py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading text-2xl font-bold mb-4">Une question ?</h2>
          <p className="text-white/70 mb-6">
            N'hésitez pas à nous contacter directement pour toute question concernant les partenariats.
          </p>
          <div className="inline-flex flex-col items-start bg-white/10 rounded-lg p-6 text-left">
            <p className="font-medium mb-2">ITERCATUS</p>
            <p className="text-white/70 text-sm">
              Attention Gilles Fouque<br />
              Quartier les 3 Fonds<br />
              13124 Peypin
            </p>
            <a 
              href={`mailto:${PUBLIC_EMAILS.partners}`} 
              className="text-accent hover:underline mt-3 flex items-center gap-2"
            >
              <Mail className="h-4 w-4" />
              {PUBLIC_EMAILS.partners}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
